import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  CpuChipIcon,
  GlobeAltIcon,
  HandRaisedIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import {
  PageHero,
  SectionHeading,
} from '../components/page-templates/SitePageTemplates';

const values = [
  {
    icon: HandRaisedIcon,
    title: 'Trust Through Transparency',
    description:
      'We behave like a partner, not a vendor. We communicate clearly, raise issues early and own outcomes without deflection.',
  },
  {
    icon: CpuChipIcon,
    title: 'Technical Depth, Human Delivery',
    description:
      'We operate in a highly technical environment, yet we never lose sight of the people behind the projects. Compliance rigour and operational discipline are delivered with responsiveness and respect.',
  },
  {
    icon: ArrowTrendingUpIcon,
    title: 'Continuous Progress',
    description:
      'The ICT sector evolves constantly. So do we. We refine processes, strengthen systems and build scalable models that grow with our clients.',
  },
];

const whoWeServe = [
  'MSPs, systems integrators and resellers managing multi-country rollouts',
  'ICT asset management platforms handling recurring device cycles',
  'Distributors requiring structured cross-border execution',
  'Technology vendors expanding into regulated markets',
];

const leadership = [
  {
    initials: 'ICT',
    name: 'Ibn Talal',
    title: 'Founder & Director',
    description:
      "Ibn founded TrueLog with the conviction that ICT logistics could be clearer, faster and more accountable. He continues to drive the company's focus on transparency, execution discipline and long-term client trust.",
  },
  {
    initials: 'VP',
    name: 'Vigneswaran Packirisamy',
    title: 'Co-Founder & Operations Director',
    description:
      'Vigneswaran oversees regional execution across APAC. His leadership ensures operational precision, compliance integrity and seamless coordination across borders.',
  },
];

const commitments = [
  'Clear communication',
  'Reliable execution',
  'Built-in compliance',
  'Real accountability',
];

const AboutUs: React.FC = () => {
  return (
    <div className="bg-white">
      <PageHero
        eyebrow="About TrueLog"
        title="A Team Making Complex ICT Moves Feel Simple."
        highlight="Feel Simple."
        description="Since 2015, TrueLog has helped organisations move, manage and control ICT hardware across borders with clarity, speed and accountability."
        heroIcon={GlobeAltIcon}
        accentIcon={ShieldCheckIcon}
        actions={[
          { href: '/contact-us', label: 'Get a Quote' },
          { href: '/services', label: 'Explore our Solutions', variant: 'secondary' },
        ]}
      />

      <section className="bg-white py-20">
        <div className="container-custom grid gap-10 lg:grid-cols-[1fr_1fr]">
          <div>
            <SectionHeading
              eyebrow="Our Team"
              title="One TrueLog Team"
              description="People-first, fast-moving, and accountable — a team that takes the work seriously without taking itself too seriously."
              align="left"
            />
          </div>
          <div className="space-y-5">
            <p className="text-base leading-8 text-slate-600">
              Since 2015, we've supported organisations managing ICT assets across borders and complex regulatory environments. Founded by Ibn Talal and Vigneswaran Packirisamy, our culture is people-first, fast-moving, and accountable.
            </p>
            <p className="text-base leading-8 text-slate-600">
              We take the work seriously but not ourselves — celebrating wins, bringing positive energy, and creating a place people enjoy showing up to.
            </p>
            <p className="text-base leading-8 text-slate-600">
              Our team brings deep expertise across ICT logistics, lifecycle management, compliance, and global operations. We execute reliably at scale.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Why We Exist"
            title="Managing ICT Logistics is Increasingly Complex & Risky"
            description="We exist to be a single, reliable partner that takes ownership of the full ICT logistics journey."
          />

          <div className="mx-auto mt-4 grid max-w-5xl gap-6">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-[28px] border border-slate-200/80 bg-white p-7 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.3)]"
            >
              <p className="text-base leading-8 text-slate-600">
                As organisations expand globally, ICT hardware movement and management become increasingly fragmented and difficult to control.
              </p>
              <p className="mt-4 text-base leading-8 text-slate-600">
                Companies managing ICT hardware across borders face multiple vendors, unpredictable delays, and compliance headaches. Every day spent firefighting slows growth and increases operational risk.
              </p>
              <p className="mt-4 text-base leading-8 text-slate-600">
                We exist to be a single, reliable partner that takes ownership of the full ICT logistics journey — from cross-border compliance and shipping to on-site installation, returns, and asset lifecycle management.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Our Values"
            title="What We Stand For"
            description="The principles that shape how we work with every client, every project, every day."
          />

          <div className="grid gap-6 md:grid-cols-3">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.08 }}
                className="rounded-[28px] border border-slate-200/80 bg-white p-7 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.25)]"
              >
                <div
                  className="inline-flex rounded-2xl p-3 text-white"
                  style={{ background: 'linear-gradient(135deg, #0E9ED5 0%, #385CF2 100%)' }}
                >
                  <value.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-xl font-semibold text-slate-950">{value.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <div>
            <SectionHeading
              eyebrow="Who We Serve"
              title="Organisations operating at scale"
              description="We work with organisations that operate at scale:"
              align="left"
            />
            <p className="mt-6 text-base leading-8 text-slate-600">
              Our role is to become the operational backbone behind their growth.
            </p>
          </div>

          <div className="grid gap-4">
            {whoWeServe.map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.06 }}
                className="flex items-start gap-4 rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-[0_18px_45px_-36px_rgba(15,23,42,0.25)]"
              >
                <div
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl text-white"
                  style={{ background: 'linear-gradient(135deg, #0E9ED5 0%, #385CF2 100%)' }}
                >
                  <CheckCircleIcon className="h-5 w-5" />
                </div>
                <p className="text-sm leading-7 text-slate-700">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75 }}
            className="rounded-[32px] border border-slate-900/20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8 text-white shadow-[0_28px_70px_-42px_rgba(15,23,42,0.85)]"
          >
            <div
              className="inline-flex rounded-2xl p-3 text-white"
              style={{ background: 'linear-gradient(135deg, #0E9ED5 0%, #385CF2 100%)' }}
            >
              <SparklesIcon className="h-6 w-6" />
            </div>
            <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
              Speed In Everything We Do
            </p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Controlled momentum, not chaos.
            </h2>
          </motion.div>

          <div className="space-y-4">
            <p className="text-base leading-8 text-slate-600">Speed is not chaos. It is controlled momentum.</p>
            <p className="text-base leading-8 text-slate-600">
              From rapid quotation turnaround to guaranteed response SLAs and same-day mobilisation for urgent installations, we operate with urgency while maintaining compliance discipline.
            </p>
            <p className="text-base leading-8 text-slate-600">
              We remove the compliance burden from end users, structure complex multi-country contracts and align commercial intent with operational reality.
            </p>
            <p className="text-base leading-8 text-slate-600 font-medium">
              This is how we make global ICT logistics feel effortless.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Leadership"
            title="The people behind TrueLog"
            description="Founders leading with transparency, execution discipline, and long-term client trust."
          />

          <div className="grid gap-6 md:grid-cols-2">
            {leadership.map((leader, index) => (
              <motion.div
                key={leader.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.08 }}
                className="rounded-[30px] border border-slate-200/80 bg-white p-7 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.25)]"
              >
                <div className="flex items-start gap-5">
                  <div
                    className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl text-lg font-bold text-white"
                    style={{ background: 'linear-gradient(135deg, #0E9ED5 0%, #385CF2 100%)' }}
                  >
                    {leader.initials}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-950">{leader.name}</h3>
                    <p className="mt-1 text-sm font-medium text-[#2447d8]">{leader.title}</p>
                  </div>
                </div>
                <p className="mt-5 text-sm leading-7 text-slate-600">{leader.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Our Commitment"
            title="Be the partner that makes global ICT logistics feel effortless."
            description="That means:"
          />

          <div className="mx-auto grid max-w-4xl gap-4 sm:grid-cols-2">
            {commitments.map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.06 }}
                className="flex items-center gap-4 rounded-[24px] border border-slate-200/80 bg-slate-50 p-5"
              >
                <div
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl text-white"
                  style={{ background: 'linear-gradient(135deg, #0E9ED5 0%, #385CF2 100%)' }}
                >
                  <CheckCircleIcon className="h-5 w-5" />
                </div>
                <p className="text-base font-semibold text-slate-900">{item}</p>
              </motion.div>
            ))}
          </div>

          <p className="mx-auto mt-10 max-w-3xl text-center text-lg leading-8 text-slate-700">
            TrueLog exists to ensure that technology moves as freely as the ideas it powers.
          </p>
        </div>
      </section>

      <section className="bg-slate-950 px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[34px] border border-white/[0.12] bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 p-8 shadow-[0_34px_90px_-48px_rgba(15,23,42,0.9)] sm:p-12">
          <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Next step</p>
              <h2 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl">
                Let's make your next ICT move effortless.
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                Whether you need a quote for an upcoming rollout or want to explore the full range of services, the team is ready to help.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/contact-us"
                  className="inline-flex items-center justify-center rounded-2xl px-7 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-[0_20px_44px_-20px_rgba(14,158,213,0.95)] transition-all duration-300 hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #0E9ED5 0%, #385CF2 100%)' }}
                >
                  Get a Quote
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center rounded-2xl border border-white/[0.12] bg-white/[0.08] px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-white/[0.12]"
                >
                  Explore our Solutions
                </Link>
              </div>
            </div>

            <div className="grid gap-3">
              <div className="flex items-start gap-3 rounded-[24px] border border-white/10 bg-white/[0.06] px-4 py-4 text-sm text-slate-200">
                <UsersIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-300" />
                <span>A team that treats your rollout as their own</span>
              </div>
              <div className="flex items-start gap-3 rounded-[24px] border border-white/10 bg-white/[0.06] px-4 py-4 text-sm text-slate-200">
                <ShieldCheckIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-300" />
                <span>Compliance burden lifted off your internal teams</span>
              </div>
              <div className="flex items-start gap-3 rounded-[24px] border border-white/10 bg-white/[0.06] px-4 py-4 text-sm text-slate-200">
                <ArrowPathIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-300" />
                <span>Continuous improvement baked into delivery</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
